import { mdiTextBoxOutline } from '@lumapps/lumx/icons';
import { GLOBAL } from '@lumapps/translations';
import { ObjectValues } from '@lumapps/utils/types/ObjectValues';

import { CONTENTS } from './keys';

export const PAGE_CONTENT_ID_PREFIX = 'content-';

/** The icon to be used on custom content type without icon defined */
export const CONTENT_TYPE_ICON_FALLBACK = mdiTextBoxOutline;

export const CONTENT_PUBLICATION_STATUS = {
    DRAFT: 'draft',
    PUBLISHED: 'published',
    SCHEDULED: 'scheduled',
    EXPIRED: 'expired',
    ARCHIVE: 'archived',
} as const;

export const CONTENT_STATUS_NOTIFICATION_STATUS = {
    DRAFT: CONTENT_PUBLICATION_STATUS.DRAFT,
    SCHEDULED: CONTENT_PUBLICATION_STATUS.SCHEDULED,
    EXPIRED: CONTENT_PUBLICATION_STATUS.EXPIRED,
    ARCHIVE: CONTENT_PUBLICATION_STATUS.ARCHIVE,
    REFUSED: 'refused',
    TO_VALIDATE: 'to-validate',
    PUBLISHED_WITH_REFUSED_VERSION: 'published-with-refused-version',
    PUBLISHED_WITH_TO_VALIDATE_VERSION: 'published-with-to-validate-version',
    EXPIRES_SOON: 'expires-soon',
} as const;

export type ContentPublicationStatus = ObjectValues<typeof CONTENT_PUBLICATION_STATUS>;

export type ContentStatusWithNotification = ObjectValues<typeof CONTENT_STATUS_NOTIFICATION_STATUS>;

type ContentStatusNotificationInfo = Record<
    ContentStatusWithNotification,
    {
        title: string;
        description: string;
        notificationType: 'warning' | 'info';
    }
>;

export const CONTENT_PUBLICATION_STATUS_WITH_NOTIFICATION: ContentStatusNotificationInfo = {
    [CONTENT_STATUS_NOTIFICATION_STATUS.EXPIRED]: {
        title: CONTENTS.CONTENT_UNPUBLISHED,
        description: CONTENTS.EXPIRED_STATUS_NOTIFICATION,
        notificationType: 'warning',
    },
    [CONTENT_STATUS_NOTIFICATION_STATUS.ARCHIVE]: {
        title: CONTENTS.CONTENT_UNPUBLISHED,
        description: CONTENTS.ARCHIVED_STATUS_NOTIFICATION,
        notificationType: 'warning',
    },
    [CONTENT_STATUS_NOTIFICATION_STATUS.SCHEDULED]: {
        title: CONTENTS.CONTENT_NOT_PUBLISHED_YET,
        description: CONTENTS.SCHEDULED_STATUS_NOTIFICATION,
        notificationType: 'warning',
    },
    [CONTENT_STATUS_NOTIFICATION_STATUS.DRAFT]: {
        title: CONTENTS.CONTENT_UNPUBLISHED,
        description: CONTENTS.DRAFT_STATUS_NOTIFICATION,
        notificationType: 'warning',
    },
    [CONTENT_STATUS_NOTIFICATION_STATUS.TO_VALIDATE]: {
        title: CONTENTS.CONTENT_NOT_PUBLISHED_YET,
        description: CONTENTS.TO_VALIDATE_STATUS_NOTIFICATION,
        notificationType: 'warning',
    },
    [CONTENT_STATUS_NOTIFICATION_STATUS.REFUSED]: {
        title: CONTENTS.CONTENT_NOT_PUBLISHED_YET,
        description: CONTENTS.REFUSED_STATUS_NOTIFICATION,
        notificationType: 'warning',
    },
    [CONTENT_STATUS_NOTIFICATION_STATUS.PUBLISHED_WITH_TO_VALIDATE_VERSION]: {
        title: CONTENTS.IS_CONTENT_UP_TO_DATE,
        description: CONTENTS.PUBLISHED_TO_VALIDATE_STATUS_NOTIFICATION,
        notificationType: 'info',
    },
    [CONTENT_STATUS_NOTIFICATION_STATUS.PUBLISHED_WITH_REFUSED_VERSION]: {
        title: CONTENTS.IS_CONTENT_UP_TO_DATE,
        description: CONTENTS.PUBLISHED_WITH_DRAFT_STATUS_NOTIFICATION,
        notificationType: 'info',
    },
    [CONTENT_STATUS_NOTIFICATION_STATUS.EXPIRES_SOON]: {
        title: CONTENTS.CONTENT_EXPIRING_SOON,
        description: CONTENTS.EXPIRES_SOON_STATUS_NOTIFICATION,
        notificationType: 'info',
    },
};

/** URL anchor marking edit/create triggered from a FO page */
export const ANCHOR_FROM_FO = 'ffo';

/** Possible actions on a content that will trigger a status change */
export const CONTENT_ACTIONS = {
    PUBLISH: 'publish',
    SAVE: 'save',
    SCHEDULE: 'schedule',
    SUBMIT_FOR_REVIEW: 'submit-for-review',
} as const;

/** Lokalise keys of the possible actions on a content */
export const CONTENT_ACTIONS_LABEL = {
    [CONTENT_ACTIONS.PUBLISH]: GLOBAL.PUBLISH,
    [CONTENT_ACTIONS.SAVE]: GLOBAL.SAVE,
    [CONTENT_ACTIONS.SCHEDULE]: GLOBAL.SCHEDULE,
    [CONTENT_ACTIONS.SUBMIT_FOR_REVIEW]: CONTENTS.SUBMIT_FOR_REVIEW_LABEL,
};

export enum CONTRIBUTION_MODES {
    READER = 'READER_MODE',
    WRITER = 'WRITER_MODE',
    DESIGNER = 'EXPERT_MODE',
}
